import { CamelCaseKeys } from "@regal-voice/shared-types";
import camelCase from "lodash/camelCase";

/****************************************************************************
 * PLEASE KEEP FILE ALPHABETIZED                                            *
 * PLEASE MATCH THE FLAG KEY EXACTLY FROM LAUNCH DARKLY (INCLUDING CASING)  *
 ****************************************************************************/
export const FLAG_DEFAULTS = {
    "admin-settings": false,
    "add-queues-to-agents-page": false,
    "agent-builder-shortcuts": false,
    "agent-templates-revamp": false,
    "agents-page": false,
    "ai-agent-demo-features": false,
    "ai-agent-performance-tab-visibility": false,
    "enable-inject-past-call-context": false,
    "ai-custom-analysis-character-limit": 5000,
    "allow-update-contact-node-and-scheduled-journeys": false,
    "always-show-user-info-in-contact-drawer": false,
    "audience-page": true,
    "auto-accept-by-task-title": "",
    "auto-accept-inbound-calls-in-conversations": false,
    "block-auto-accepted-calls-when-agent-is-on-active-call": false,
    "brand-numbers": false,
    "browser-notifications": 0,
    "business-profile-third-party-loa-website-fields": false,
    "calls-agents-ratio-limit": 15,
    campaigns: false,
    "can-share-contact-lists": false,
    // Per-evaluator observability sampling config owned by agent-service. Keys are evaluator
    // names with snake_case entries, so it stays untyped here and is parsed where it is read.
    "configure-ai-observability-evaluator-sampling": {} as Record<string, unknown>,
    "contact-list-create-saved-views": false,
    "contact-list-saved-views": false,
    "contact-list": false,
    "conversation-intelligence-custom-analysis": true,
    "conversation-intelligence-score-cards": false,
    "conversations-intelligence-insights": false,
    "custom-cancel-task-enabled": true,
    "custom-draggable-intercom-button": false,
    dashboards: false,
    "data-dog-browser-logs-level": "debug",
    "data-dog-browser-logs-sample-rate": 100,
    "data-dog-browser-logs": true,
    "data-dog-rum-sample-rate": 100,
    "data-dog-rum": true,
    "disable-edit-multiple-phones-and-emails": false,
    "display-agent-desktop-permissions-page": false,
    "display-enriched-events": "",
    email: 0,
    "enable-action-sequences": false,
    "enable-admin-take-over-on-ai-calls": false,
    "enable-agent-to-reassign-task": false,
    "enable-ai-agent-clone-edit": false,
    "enable-ai-agent-variants": false,
    "enable-call-recording-pause": true,
    "enable-custom-action-header-templating": false,
    "enable-custom-action-http-methods": false,
    "enable-custom-action-secret-headers": false,
    "enable-custom-action-url-templating": false,
    "enable-delete-user": false,
    "enable-draft-agents": false,
    "enable-dsl-debug": false,
    "enable-dynamic-responsiveness": false,
    "enable-five9-ai-agent-connect": false,
    "enable-endpointing-alpha-admin-setting": false,
    "enable-five9-integration": false,
    "enable-parallel-dialing-mode": false,
    "enable-expanded-prompt-editor": false,
    "enable-progressive-dialer": false,
    "enable-simulation-run-metadata": false,
    "enable-tracker-ai-assist": false,
    "enable-update-voice-settings": false,
    "enable-transfer-to-busy-agents": false,
    "enables-the-ai-scorecards-edit": false,
    "enforce-mic-permissions": "hide",
    "has-ai-agents-demo": false,
    "has-bulk-agent-testing": false,
    "has-campaigns-dashboard": false,
    "has-copilot-helper": false,
    "has-copilot-mermaid-charts": false,
    "has-custom-functions-page": false,
    "has-ivr-demo-nodes": false,
    "has-outlook-integration": false,
    "has-pre-recorded-vm-drops": false,
    "has-self-serve-task-attributes": false,
    "has-regal-copilot-nav": false,
    "has-regal-copilot-sessions": false,
    "has-vm-drop-sub-brand-selection": false,
    "is-demoing-agentless-call": false,
    "is-demoing-custom-ai-task": false,
    "is-demoing-eight-by-eight": false,
    "is-demoing-email-task": false,
    "is-demoing-five-9": false,
    "is-demoing-nice": false,
    "is-demoing-genesys": false,
    "is-demoing-ringless-vm": false,
    "is-demoing-send-email": false,
    "is-demoing-sms-ai": false,
    "is-demoing-talkdesk": false,
    "is-demoing-voice-ai": false,
    "ivr-builder-webhook-secret-headers": false,
    "ivr-page": false,
    "ivr-show-other-methods-in-webhook-node": false,
    "journey-builder-allow-pageview-trigger": false,
    "journey-builder-assign-target-agent": false,
    "journey-builder-reminder-node": true,
    "journey-builder-scheduled-callback-node": true,
    "journey-builder-update-task": false,
    "journey-builder-webhook-secret-headers": false,
    "journey-execution-monitor": false,
    journeys: true,
    "known-worker-routing-setting": false,
    "manager-actions-config": {
        barge: false,
        listen: false,
        whisper: false,
        takeOver: false,
    },
    "make-gryphon-integration-self-serve": false,
    "max-number-business-profiles": 1,
    "mock-regal-live-alternative": false,
    "multi-state-demo-features": false,
    "multi-state-sticky-notes": false,
    "multi-tab-detection": true,
    "new-product-alert-url": "",
    "no-autocomplete-dialpad": false,
    // "YYYY-MM-DD" cutoff for the observability date picker; empty means no lower bound.
    "observability-earliest-start-date": "",
    "observability-page-behavior": {
        enabled: false,
        sections: {
            alerts: true,
            contactIssues: false,
            factMetrics: true,
            // guardrailBreach and wrongFunctionCall default to visible: variations that omit
            // them keep both tiles, and only an explicit false hides them.
            guardrailBreach: true,
            llmMetrics: true,
            newEvaluatorMetrics: false,
            wrongFunctionCall: true,
        },
        useMockData: true,
    },
    "okta-session-idle-refresh": false,
    "outlook-html-signature": false,
    "predictive-dialer": false,
    "quiet-hours-crud": false,
    "queue-transfer-hold-music": false,
    "queues-page": false,
    "use-regal-amd-for-ai-agents": false,
    recentActivity: false,
    "regal-live-dummy-data": false,
    "agents-live-stats": {
        containmentRate: false,
        isAiAgent: true,
    },
    "regal-live-page": false,
    "rollout-ui-agent-pronunciation-modal": false,
    "rollout-ui-enable-moments": true,
    "rollout-ui-knowledge-base": false,
    "rollout-ui-knowledge-base-test": false,
    "rollout-ui-unauthorized-interception": false,
    "salesforce-integration": false,
    "save-as-ai-agent-template": false,
    ScheduleSMSTask: false,
    segments: false,
    "self-serve-team-updates-and-deletes": false,
    "send-agent-desktop-attributes-as-datetime": false,
    "send-event-feature": false,
    "send-event-feature-audience-page": false,
    "session-replay-sample-rate": 100,
    "should-group-copilot-agent-chats": false,
    "show-agent-chat-transcripts": false,
    "show-agent-desktop-stats-advanced": false,
    "show-agent-desktop-stats": false,
    "show-ai-disposition-llm": false,
    "show-api-key": false,
    "show-brand-secrets": false,
    "show-branded-numbers": false,
    "show-business-profiles": false,
    "show-business-profile-primary-contact": false,
    "show-custom-user-attributes": false,
    "show-custom-attributes-on-user": false,
    "show-delete-contact-button": false,
    "show-profile-versions": false,
    "show-dtmf-settings": false,
    "show-external-transfer-phonebook-integrations": false,
    "show-gemini-3.6": false,
    "show-gpt-5-6-models": false,
    "show-maintenance": false,
    "show-max-ring-time": true,
    "show-multi-select-variable-type": false,
    "show-realtime-model": false,
    "show-request-payload-endpoint": false,
    "show-routing-rules": false,
    "show-sentiment-in-activity-feed": false,
    "show-speech-normalization": false,
    "show-end-call-skip-goodbye": false,
    "show-transcript-summary-in-activity-feed": false,
    "show-trust-overview": false,
    "show-user-management": false,
    "show-zendesk-widget": false,
    "snooze-task": true,
    "sse-connection-diagnostic": false,
    "task-recreation": false,
    "task-routing-new-page-route": false,
    tasks: true,
    "transcript-items-sort-by-start": false,
    "transcriptions-page": false,
    "unified-vm-config-power-progressive-dial": false,
    "use-ai-agent-admin-configs": false,
    "use-backchanneling": false,
    "use-contact-data-fields": false,
    "use-dynamic-transfer": false,
    "updated-agent-templates-page": false,
    "use-sub-brand-for-lookup": false,
    "voice-sdk-close-protection": false,
    "voice-sdk-dd-logger": false,
    "voice-sdk-log-level": "SILENT",
    "workbench-poc": false,
    "use-enhanced-voicemail-drop": false,
    "use-tasks-backlog-limit-from-campaigns-table": false,
    "dynamic-campaign-priority": false,
    "agent-claims": false,
    "show-audio-file-selector-for-asap-callback": false,
};
/****************************************************************************
 * PLEASE KEEP FILE ALPHABETIZED                                            *
 * PLEASE MATCH THE FLAG KEY EXACTLY FROM LAUNCH DARKLY (INCLUDING CASING)  *
 ****************************************************************************/

// we have to hardcode some flags that are created with uppercase acronyms in LD but are not handled properly by our CamelCaseKeys utility
// "show-gemini-3.6" also needs hardcoding: our CamelCaseKeys type doesn't strip the "." separator, so it would
// otherwise resolve to "showGemini3.6" instead of the runtime lodash camelCase result "showGemini36".
export type FlagTypes = CamelCaseKeys<typeof FLAG_DEFAULTS> & {
    scheduleSmsTask: boolean;
    showGemini36: boolean;
};

export type Flag = keyof FlagTypes;

export const FLAGS_DEFAULTS_FORMATTED = Object.entries(FLAG_DEFAULTS).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
        acc[camelCase(key)] = value;
        return acc;
    },
    {}
) as FlagTypes;
